// VendorsDashboard — Bridge Inbox first. Vendors run on WOs coming from
// property managers. Hero = Bridge Inbox pulse + AI Bid drafts CTA.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  PlugsConnected, Sparkle, MapPin, Clock, ArrowRight, CheckCircle,
  Warning, House, PaperPlaneTilt, EnvelopeSimple,
} from "@phosphor-icons/react";

const STATUS_CHIP = {
  scheduled:  { cls: "bg-slate-200 text-slate-700",    label: "NEW WO" },
  assigned:   { cls: "bg-teal-100 text-teal-800",      label: "ACCEPTED" },
  en_route:   { cls: "bg-cyan-100 text-cyan-800",      label: "EN ROUTE" },
  in_progress:{ cls: "bg-amber-100 text-amber-800",    label: "IN PROGRESS" },
  completed:  { cls: "bg-emerald-100 text-emerald-800",label: "SYNCED BACK" },
};

export default function VendorsDashboard() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [j, b] = await Promise.all([
          api.get("/jobs").catch(() => ({ data: [] })),
          api.get("/integrations/bridge/inbox").catch(() => ({ data: { jobs: [] } })),
        ]);
        setJobs(Array.isArray(j.data) ? j.data : []);
        setInbox(b.data?.jobs || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const nextStop = jobs.find(j => j.status === "en_route") || jobs.find(j => j.status === "assigned") || jobs.find(j => j.status === "scheduled");
  const stops = jobs.filter(j => j.id !== nextStop?.id).slice(0, 4);
  const funnel = {
    inbox:     inbox.length,
    accepted:  jobs.filter(j => ["assigned","scheduled"].includes(j.status)).length,
    working:   jobs.filter(j => ["en_route","in_progress"].includes(j.status)).length,
    synced:    jobs.filter(j => j.status === "completed").length,
  };

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4" data-testid="vendors-dashboard">
        {/* HERO — Bridge Inbox pulse */}
        <div
          onClick={() => nav("/admin/bridge-inbox")}
          data-testid="vendors-hero-inbox"
          className="cursor-pointer relative rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-teal-900 to-slate-950 p-6 sm:p-8 text-white hover:shadow-xl transition group"
        >
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-6 right-6"><PlugsConnected weight="fill" size={200}/></div>
          </div>
          <div className="relative">
            <div className="text-[10px] font-black uppercase tracking-widest text-teal-200 inline-flex items-center gap-1.5">
              <PlugsConnected weight="fill" size={11}/> Bridge Inbox · Every PM
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight mt-2 tracking-tight" data-testid="vendors-hero-title">
              {funnel.inbox > 0 ? (
                <>{funnel.inbox} new WO{funnel.inbox === 1 ? "" : "s"} <span className="text-teal-300">from PMs.</span></>
              ) : (
                <>Bridge Inbox <span className="text-teal-300">is clear.</span></>
              )}
            </h1>
            <div className="text-sm text-teal-100/80 mt-2 max-w-md">
              Every PM software you connect drops WOs here. Accept, bid, and sync photos back — no double-entry.
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-teal-400 text-slate-900 font-black uppercase text-[11px] tracking-widest group-hover:scale-105 transition">
                <EnvelopeSimple weight="bold" size={14}/> Open Bridge Inbox
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); nav("/admin/drafts"); }}
                data-testid="vendors-hero-drafts"
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white font-black uppercase text-[11px] tracking-widest">
                <Sparkle weight="fill" size={14}/> AI Bid Drafts
              </button>
            </div>
          </div>
        </div>

        {/* NEXT STOP */}
        {nextStop && (
          <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="vendors-next-stop">
            <div className="px-4 py-2 bg-slate-900 text-white flex items-center gap-2">
              <MapPin weight="fill" size={14}/>
              <div className="text-[10px] font-black uppercase tracking-widest">Next WO</div>
              <StatusChip s={nextStop.status}/>
            </div>
            <div className="p-4">
              <div className="font-display font-black text-xl leading-tight" data-testid="next-stop-title">{nextStop.title}</div>
              <div className="text-sm text-slate-500 font-medium inline-flex items-center gap-1 mt-1">
                <House size={12}/> {nextStop.property_address}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={`https://maps.google.com/?q=${encodeURIComponent(nextStop.property_address || "")}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="next-stop-nav"
                  className="h-10 px-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  <MapPin weight="fill" size={13}/> Navigate
                </a>
                <button type="button" onClick={() => nav(`/admin/jobs?open=${nextStop.id}`)}
                  data-testid="next-stop-open"
                  className="h-10 px-3 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-900 text-slate-800 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  Open WO <ArrowRight size={12}/>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ACTIVE WORK ORDERS */}
        <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="vendors-active">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 inline-flex items-center gap-1.5">
              <Clock size={12}/> Active work orders
            </div>
            <button type="button" onClick={() => nav("/admin/jobs")}
              data-testid="vendors-all-jobs"
              className="text-[10px] font-black uppercase tracking-widest text-teal-600 hover:text-teal-800 inline-flex items-center gap-1">
              All WOs <ArrowRight size={10}/>
            </button>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500 font-medium">Loading…</div>
          ) : stops.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 font-medium" data-testid="vendors-no-stops">
              No active WOs. Check the Bridge Inbox for new work.
            </div>
          ) : (
            stops.map((j, i) => (
              <div key={j.id} className={`p-3 flex items-center gap-3 ${i > 0 ? "border-t border-slate-100" : ""}`}
                    data-testid={`vendors-wo-${j.id}`}>
                <div className="h-9 w-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                  <PlugsConnected weight="fill" size={16}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm truncate">{j.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">{j.property_address}</div>
                </div>
                <StatusChip s={j.status}/>
              </div>
            ))
          )}
        </div>

        {/* FUNNEL */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="vendors-funnel">
          <FunnelTile label="Bridge inbox" value={funnel.inbox} icon={<EnvelopeSimple size={14}/>} accent="slate"/>
          <FunnelTile label="Accepted" value={funnel.accepted} icon={<Warning size={14}/>} accent="teal"/>
          <FunnelTile label="Working" value={funnel.working} icon={<PaperPlaneTilt size={14}/>} accent="cyan"/>
          <FunnelTile label="Synced back" value={funnel.synced} icon={<CheckCircle size={14}/>} accent="emerald"/>
        </div>
      </div>
    </AdminShell>
  );
}

function StatusChip({ s }) {
  const m = STATUS_CHIP[s] || { cls: "bg-slate-100 text-slate-600", label: (s || "").toUpperCase() };
  return <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}

function FunnelTile({ label, value, icon, accent }) {
  const bg = {
    slate:   "bg-slate-50 border-slate-200 text-slate-800",
    teal:    "bg-teal-50 border-teal-200 text-teal-900",
    cyan:    "bg-cyan-50 border-cyan-200 text-cyan-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  }[accent] || "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`rounded-xl border-2 p-3 ${bg}`}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="font-display font-black text-2xl mt-0.5">{value}</div>
    </div>
  );
}
